import { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import engagementService from '../../../services/engagementService';

const FacebookIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const XIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#000000">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ShareButton = ({ articleId, articleTitle, shareTextPrefix = 'اقرأ', variant = 'floating' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef(null);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `${shareTextPrefix}: ${articleTitle}`;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const trackShare = (platform) => {
    engagementService.trackShare(articleId, platform).catch(() => {});
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    trackShare('facebook');
    setIsOpen(false);
  };

  const handleShareX = () => {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
    trackShare('x');
    setIsOpen(false);
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
    window.open(url, '_blank', 'width=600,height=400');
    trackShare('whatsapp');
    setIsOpen(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackShare('copy');
      setTimeout(() => {
        setCopied(false);
        setIsOpen(false);
      }, 1500);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      trackShare('copy');
      setTimeout(() => {
        setCopied(false);
        setIsOpen(false);
      }, 1500);
    }
  };

  const shareOptions = [
    {
      id: 'facebook',
      label: 'فيسبوك',
      icon: <FacebookIcon size={18} />,
      action: handleShareFacebook,
    },
    {
      id: 'x',
      label: 'إكس',
      icon: <XIcon size={18} />,
      action: handleShareX,
    },
    {
      id: 'whatsapp',
      label: 'واتساب',
      icon: <WhatsAppIcon size={18} />,
      action: handleShareWhatsApp,
    },
    {
      id: 'copy',
      label: copied ? 'تم النسخ!' : 'نسخ الرابط',
      icon: (
        <Icon
          name={copied ? 'Check' : 'Link'}
          size={18}
          color={copied ? '#7D8471' : '#C5A059'}
        />
      ),
      action: handleCopyLink,
    },
  ];

  const isInline = variant === 'inline';

  return (
    <div
      className={isInline ? 'relative inline-block' : 'fixed top-10 left-10 z-40'}
      ref={dropdownRef}
    >
      {/* Share Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={
          isInline
            ? 'flex items-center justify-center gap-2 text-[#C5A059] hover:text-[#A68548] transition-colors duration-300 p-0'
            : 'w-14 h-14 rounded-full transition-all duration-300 flex items-center justify-center'
        }
        style={
          isInline
            ? undefined
            : {
                backgroundColor: isOpen
                  ? 'rgba(197, 160, 89, 0.2)'
                  : 'rgba(197, 160, 89, 0.1)',
              }
        }
        aria-label="مشاركة"
      >
        <Icon
          name={isOpen ? 'X' : 'Share2'}
          size={isInline ? 20 : 24}
          color="#C5A059"
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`absolute rounded-2xl py-2 min-w-[180px] overflow-hidden z-50 ${
            isInline ? 'top-full right-0 mt-2' : 'top-16 left-0'
          }`}
          style={{
            backgroundColor: '#FEFDFB',
            boxShadow: '0 8px 30px rgba(139, 105, 20, 0.12)',
            border: '1px solid rgba(197, 160, 89, 0.15)',
          }}
        >
          {shareOptions.map((option) => (
            <button
              key={option.id}
              onClick={option.action}
              className="w-full flex items-center gap-3 px-5 py-3 transition-all duration-200 hover:bg-[rgba(197,160,89,0.06)]"
            >
              <span className="flex-shrink-0">{option.icon}</span>
              <span
                className="text-sm font-medium"
                style={{
                  fontFamily: 'Amiri, serif',
                  color: option.id === 'copy' && copied ? '#7D8471' : '#2C2B29',
                }}
              >
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShareButton;
